import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Heading,
  Button,
  Card,
  CardBody,
  CardHeader,
  Text,
  Badge,
  HStack,
  VStack,
  IconButton,
  useDisclosure,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Skeleton,
  Alert,
  AlertIcon,
  Avatar,
  AvatarGroup,
  Tooltip,
  Progress,
  Divider,
  Checkbox,
  ButtonGroup,
} from '@chakra-ui/react';
import { 
  FiMoreVertical, 
  FiUsers, 
  FiMic, 
  FiMicOff, 
  FiSettings,
  FiVolume2,
  FiVolumeX,
  FiLogIn,
  FiLogOut,
  FiUserPlus,
  FiUserMinus,
  FiEdit,
  FiTrash2,
  FiMove,
  FiArchive,
  FiMail,
  FiHeadphones
} from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import { useVoiceService, VoiceGroup, VoiceRoom } from '../../hooks/useVoiceService';
import { useVoiceStatusStream } from '../../hooks/useVoiceStatusStream';
import { useUserProfile } from '../../contexts/userprofile/UserProfileContext';

const getGroupTypeLabel = (type: number) => {
  switch (type) {
    case 1: return 'Text';
    case 2: return 'Voice';
    case 3: return 'Video';
    case 4: return 'Streaming';
    default: return 'Unknown';
  }
};

const getGroupStatusLabel = (status: number) => {
  switch (status) {
    case 1: return 'Active';
    case 2: return 'Inactive';
    case 3: return 'Archived';
    case 4: return 'Locked';
    default: return 'Unknown';
  }
};

const getGroupStatusColor = (status: number) => {
  switch (status) {
    case 1: return 'green';
    case 2: return 'gray';
    case 3: return 'orange';
    case 4: return 'red';
    default: return 'gray';
  }
};

const getMemberRoleLabel = (role: number) => {
  switch (role) {
    case 1: return 'Member';
    case 2: return 'Moderator';
    case 3: return 'Admin';
    case 4: return 'Owner';
    default: return 'Guest';
  }
};

export const VoiceGroupPage: React.FC = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const [group, setGroup] = useState<VoiceGroup | null>(null);
  const [room, setRoom] = useState<VoiceRoom | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [batchLoading, setBatchLoading] = useState(false);
  
  const { profile } = useUserProfile();
  const navigate = useNavigate();
  const { 
    getVoiceGroups, 
    getVoiceGroup,
    getRoom,
    joinVoiceGroup, 
    leaveVoiceGroup,
    muteMember,
    unmuteMember,
    deafenMember,
    undeafenMember,
    transferOwnership
  } = useVoiceService();

  // WebSocket connection for real-time updates
  console.log('VoiceGroupPage: Setting up useVoiceStatusStream with:', {
    groupId,
    enabled: !!groupId,
    hasGroup: !!group
  });

  const {
    groupStatusUpdates,
    initialGroupsData,
    hasInitialData,
    isConnected,
    getLatestGroupStatus
  } = useVoiceStatusStream({
    groupIds: groupId ? [groupId] : [],
    roomIds: [], // We'll use groupId first, roomId can be added later for room-level updates
    includeInitialState: true,
    enabled: !!groupId,
  });

  console.log('VoiceGroupPage: WebSocket state:', {
    isConnected,
    hasInitialData,
    groupStatusUpdatesCount: groupStatusUpdates.length,
    initialGroupsDataCount: initialGroupsData.length
  });

  // Check if current user is owner
  const isOwner = group?.owner === profile?.id || 
                  group?.owner?.id === profile?.id ||
                  group?.members?.members?.find(m => 
                    m.userprofile_id === profile?.id && m.role === 4 // Owner role
                  );
  const isAdmin = group?.members?.members?.find(m => 
    m.userprofile_id === profile?.id && (m.role === 3 || m.role === 4) // Admin or Owner
  );
  
  // Debug logging
  useEffect(() => {
    if (group && profile) {
      console.log('Owner check:', {
        groupOwner: group.owner,
        profileId: profile.id,
        isOwner,
        isAdmin,
        members: group.members?.members
      });
    }
  }, [group, profile, isOwner, isAdmin]);

  const loadGroupAndRoom = useCallback(async () => {
    if (!groupId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Load group details
      const groupResponse = await getVoiceGroup(groupId);
      console.log('Group loaded:', groupResponse.voice_group);
      console.log('Current profile:', profile);
      setGroup(groupResponse.voice_group || null);
      
      // Load room details if we have room_id
      if (groupResponse.voice_group?.room_id) {
        try {
          const roomResponse = await getRoom(groupResponse.voice_group.room_id);
          setRoom(roomResponse.room || null);
        } catch (roomErr) {
          // Room loading failed, but don't fail the whole page
          console.warn('Failed to load room details:', roomErr);
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load group details');
    } finally {
      setLoading(false);
    }
  }, [groupId, getVoiceGroup, getRoom]);

  useEffect(() => {
    loadGroupAndRoom();
  }, [loadGroupAndRoom]);

  // Use initial data from WebSocket if available, fallback to REST API
  useEffect(() => {
    if (hasInitialData && initialGroupsData.length > 0) {
      const initialGroup = initialGroupsData.find(g => g.id === groupId);
      if (initialGroup) {
        console.log('Using initial group data from WebSocket:', initialGroup);
        setGroup(initialGroup);
        setLoading(false);
        
        // Load room details if we have room_id
        if (initialGroup.room_id) {
          getRoom(initialGroup.room_id)
            .then(roomResponse => {
              setRoom(roomResponse.room || null);
            })
            .catch(roomErr => {
              console.warn('Failed to load room details:', roomErr);
            });
        }
      }
    }
  }, [hasInitialData, initialGroupsData, groupId, getRoom]);

  // Handle real-time group updates
  useEffect(() => {
    if (groupStatusUpdates.length === 0 || !group) return;

    const latestUpdate = groupStatusUpdates
      .filter(update => update.group_id === groupId)
      .slice(-1)[0];

    if (latestUpdate) {
      setGroup(prevGroup => {
        if (!prevGroup) return prevGroup;
        
        // Update group data with real-time changes
        return {
          ...prevGroup,
          ...latestUpdate.group,
          members: latestUpdate.group?.members || prevGroup.members,
          settings: {
            ...prevGroup.settings,
            ...latestUpdate.group?.settings,
          }
        };
      });
    }
  }, [groupStatusUpdates, groupId, group]);

  // Update left sidebar with group management actions
  useEffect(() => {
    if (!group) return;

    const sidebarItems = [
      {
        label: 'All Rooms',
        path: '/voice/rooms',
      },
      {
        label: room?.name || 'Room',
        path: `/voice/rooms/${group.room_id}`,
      },
      {
        label: group.name,
        path: `/voice/groups/${groupId}`,
        active: true,
      },
    ];

    // Add management actions for owners/admins
    if (isOwner || isAdmin) {
      sidebarItems.push(
        { label: '---', path: '#', divider: true } as any,
        {
          label: 'Group Settings',
          path: '#',
          action: () => navigate(`/voice/groups/${groupId}/settings`),
          icon: 'FiSettings',
        } as any,
        {
          label: 'Manage Members',
          path: '#',
          action: () => navigate(`/voice/groups/${groupId}/members`),
          icon: 'FiUsers',
        } as any,
        {
          label: 'Invitations',
          path: '#',
          action: () => navigate(`/voice/groups/${groupId}/invites`),
          icon: 'FiMail',
        } as any,
      );

      if (isOwner) {
        sidebarItems.push(
          {
            label: 'Move Group',
            path: '#',
            action: () => {/* TODO: Implement move modal */},
            icon: 'FiMove',
          } as any,
          {
            label: 'Transfer Ownership',
            path: '#',
            action: () => {/* TODO: Implement transfer modal */},
            icon: 'FiEdit',
          } as any,
          { label: '---', path: '#', divider: true } as any,
          {
            label: 'Archive Group',
            path: '#',
            action: () => {/* TODO: Implement archive */},
            icon: 'FiArchive',
            style: 'warning',
          } as any,
          {
            label: 'Delete Group',
            path: '#',
            action: () => {/* TODO: Implement delete */},
            icon: 'FiTrash2',
            style: 'danger',
          } as any,
        );
      }
    }

    window.updateLeftSidebar?.(sidebarItems);

    return () => window.updateLeftSidebar?.([]);
  }, [groupId, group, room, isOwner, isAdmin, navigate]);

  // Calculate member stats
  const memberCount = group?.members?.members?.length || 0;
  const maxMembers = group?.settings?.max_members || 20;
  const occupancyRate = (memberCount / maxMembers) * 100;

  // Update right sidebar with connection status and group actions
  useEffect(() => {
    if (!group) return;

    window.updateRightSidebar?.(
      <VStack spacing={4}>
        <Box>
          <Text fontSize="sm" fontWeight="medium" mb={2}>
            Connection Status
          </Text>
          <Badge colorScheme={isConnected ? 'green' : 'red'}>
            {isConnected ? 'Connected' : 'Disconnected'}
          </Badge>
        </Box>

        {/* Group Actions */}
        <VStack spacing={2} width="full">
          {profile && (() => {
            const currentMember = group.members?.members?.find(m => m.userprofile_id === profile.id);
            
            if (currentMember) {
              return (
                <Button
                  leftIcon={<FiLogOut />}
                  colorScheme="red"
                  variant="outline"
                  width="full"
                  onClick={() => leaveVoiceGroup(groupId)}
                >
                  Leave Group
                </Button>
              );
            } else {
              return (
                <Button
                  leftIcon={<FiLogIn />}
                  colorScheme="green"
                  width="full"
                  onClick={() => joinVoiceGroup(groupId)}
                >
                  Join Group
                </Button>
              );
            }
          })()}
        </VStack>

        {/* Admin Actions */}
        {(isOwner || isAdmin) && (
          <VStack spacing={2} width="full">
            <Text fontSize="sm" fontWeight="medium">
              Admin Actions
            </Text>
            
            <Button
              leftIcon={<FiSettings />}
              colorScheme="blue"
              variant="outline"
              size="sm"
              width="full"
              onClick={() => navigate(`/voice/groups/${groupId}/settings`)}
            >
              Group Settings
            </Button>
            
            <Button
              leftIcon={<FiMicOff />}
              colorScheme="orange"
              variant="outline"
              size="sm"
              width="full"
              onClick={async () => {
                if (confirm('Are you sure you want to mute all members?')) {
                  await muteMember(groupId, 'all', 'Mute all members');
                  await loadGroupAndRoom();
                }
              }}
            >
              Mute All
            </Button>
            
            <Button
              leftIcon={<FiMic />}
              colorScheme="green"
              variant="outline"
              size="sm"
              width="full"
              onClick={async () => {
                await unmuteMember(groupId, 'all');
                await loadGroupAndRoom();
              }}
            >
              Unmute All
            </Button>
          </VStack>
        )}

        {/* Group Stats */}
        <VStack align="stretch" spacing={1} width="full">
          <Text fontSize="sm" fontWeight="medium">
            Group Stats
          </Text>
          <Text fontSize="xs" color="gray.500">
            Members: {memberCount}/{maxMembers}
          </Text>
          <Text fontSize="xs" color="gray.500">
            Occupancy: {occupancyRate.toFixed(1)}%
          </Text>
          {hasInitialData && (
            <Text fontSize="xs" color="blue.500">
              Data: WebSocket
            </Text>
          )}
        </VStack>
      </VStack>
    );

    return () => window.updateRightSidebar?.(null);
  }, [group, isConnected, hasInitialData, profile, isOwner, isAdmin, groupId, navigate, joinVoiceGroup, leaveVoiceGroup, muteMember, unmuteMember, loadGroupAndRoom, memberCount, maxMembers, occupancyRate]);


  if (!groupId) {
    return (
      <Box p={6}>
        <Alert status="error">
          <AlertIcon />
          Group ID is required
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box p={6}>
        <Skeleton height="40px" mb={6} />
        <Skeleton height="200px" mb={6} />
        <Skeleton height="300px" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={6}>
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      </Box>
    );
  }

  if (!group) {
    return (
      <Box p={6}>
        <Alert status="warning">
          <AlertIcon />
          Group not found
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={6}>
      {/* Group Header */}
      <Card mb={6}>
        <CardHeader>
          <HStack justify="space-between">
            <VStack align="start" spacing={1}>
              <HStack>
                <Heading size="lg">{group.name}</Heading>
                {group.is_default && (
                  <Badge colorScheme="purple" variant="subtle">
                    Default
                  </Badge>
                )}
              </HStack>
              <HStack>
                <Badge colorScheme="blue" variant="subtle">
                  {getGroupTypeLabel(group.type)}
                </Badge>
                <Badge colorScheme={getGroupStatusColor(group.status)} variant="subtle">
                  {getGroupStatusLabel(group.status)}
                </Badge>
              </HStack>
            </VStack>
            
            <HStack>
              <Button
                leftIcon={<FiLogIn />}
                colorScheme="green"
                onClick={() => joinVoiceGroup(groupId)}
              >
                Join Group
              </Button>
              
              {(isOwner || isAdmin) && (
                <Menu>
                  <MenuButton
                    as={IconButton}
                    icon={<FiMoreVertical />}
                    variant="outline"
                  />
                  <MenuList>
                    <MenuItem icon={<FiSettings />}>
                      Group Settings
                    </MenuItem>
                    <MenuItem icon={<FiUsers />}>
                      Manage Members
                    </MenuItem>
                    {isOwner && (
                      <>
                        <MenuItem icon={<FiMove />}>
                          Move Group
                        </MenuItem>
                        <MenuItem icon={<FiArchive />}>
                          Archive Group
                        </MenuItem>
                      </>
                    )}
                  </MenuList>
                </Menu>
              )}
            </HStack>
          </HStack>
        </CardHeader>
        
        <CardBody>
          <VStack align="stretch" spacing={4}>
            {group.description && (
              <Text color="gray.600">{group.description}</Text>
            )}
            
            <Divider />
            
            <VStack align="stretch" spacing={2}>
              <HStack justify="space-between">
                <Text fontSize="sm" fontWeight="medium">
                  Members ({memberCount}/{maxMembers})
                </Text>
                <HStack spacing={4}>
                  <HStack>
                    {isConnected ? <FiVolume2 color="green" /> : <FiVolumeX color="red" />}
                    <Text fontSize="sm" color={isConnected ? 'green.500' : 'red.500'}>
                      {isConnected ? 'Live' : 'Offline'}
                    </Text>
                  </HStack>
                  <HStack>
                    {group.settings?.is_public ? <FiMic /> : <FiMicOff />}
                    <Text fontSize="sm">
                      {group.settings?.is_public ? 'Public' : 'Private'}
                    </Text>
                  </HStack>
                </HStack>
              </HStack>
              
              <Progress 
                value={occupancyRate} 
                colorScheme={occupancyRate > 80 ? 'red' : occupancyRate > 60 ? 'yellow' : 'green'}
                size="sm"
                hasStripe
              />
            </VStack>
          </VStack>
        </CardBody>
      </Card>

      {/* Members List */}
      {group.members?.members && group.members.members.length > 0 && (
        <Card>
          <CardHeader>
            <VStack align="stretch" spacing={3}>
              <HStack justify="space-between">
                <Heading size="md">Members</Heading>
                {(isOwner || isAdmin) && (
                  <HStack spacing={2}>
                    <Checkbox
                      isChecked={selectedMembers.length === group.members.members.filter(m => m.userprofile_id !== profile?.id).length}
                      onChange={(e) => {
                        if (e.target.checked) {
                          // Select all except current user
                          const allUserIds = group.members.members
                            .filter(m => m.userprofile_id !== profile?.id)
                            .map(m => m.user_id);
                          setSelectedMembers(allUserIds);
                        } else {
                          setSelectedMembers([]);
                        }
                      }}
                    >
                      Select All
                    </Checkbox>
                    {selectedMembers.length > 0 && (
                      <HStack spacing={2}>
                        <Text fontSize="sm" color="gray.500">
                          {selectedMembers.length} selected
                        </Text>
                        <Button 
                          size="xs" 
                          variant="ghost" 
                          onClick={() => setSelectedMembers([])}
                        >
                          Clear
                        </Button>
                      </HStack>
                    )}
                  </HStack>
                )}
              </HStack>
              
              {(isOwner || isAdmin) && selectedMembers.length > 0 && (
                <ButtonGroup size="sm" flexWrap="wrap">
                  <Button 
                    colorScheme="orange" 
                    isLoading={batchLoading}
                    onClick={async () => {
                      setBatchLoading(true);
                      try {
                        await muteMember(groupId, selectedMembers, 'Batch mute');
                        await loadGroupAndRoom(); // Reload to see updated status
                        setSelectedMembers([]);
                      } finally {
                        setBatchLoading(false);
                      }
                    }}
                  >
                    Mute Selected ({selectedMembers.length})
                  </Button>
                  <Button 
                    colorScheme="blue" 
                    isLoading={batchLoading}
                    onClick={async () => {
                      setBatchLoading(true);
                      try {
                        await unmuteMember(groupId, selectedMembers);
                        await loadGroupAndRoom(); // Reload to see updated status
                        setSelectedMembers([]);
                      } finally {
                        setBatchLoading(false);
                      }
                    }}
                  >
                    Unmute Selected
                  </Button>
                  <Button 
                    colorScheme="yellow" 
                    isLoading={batchLoading}
                    onClick={async () => {
                      setBatchLoading(true);
                      try {
                        await deafenMember(groupId, selectedMembers, 'Batch deafen');
                        await loadGroupAndRoom(); // Reload to see updated status
                        setSelectedMembers([]);
                      } finally {
                        setBatchLoading(false);
                      }
                    }}
                  >
                    Deafen Selected
                  </Button>
                  <Button 
                    colorScheme="green" 
                    isLoading={batchLoading}
                    onClick={async () => {
                      setBatchLoading(true);
                      try {
                        await undeafenMember(groupId, selectedMembers);
                        await loadGroupAndRoom(); // Reload to see updated status
                        setSelectedMembers([]);
                      } finally {
                        setBatchLoading(false);
                      }
                    }}
                  >
                    Undeafen Selected
                  </Button>
                  <Button 
                    colorScheme="red" 
                    variant="outline"
                    isLoading={batchLoading}
                    onClick={async () => {
                      if (confirm('Are you sure you want to mute all members?')) {
                        setBatchLoading(true);
                        try {
                          await muteMember(groupId, 'all', 'Mute all members');
                          await loadGroupAndRoom(); // Reload to see updated status
                        } finally {
                          setBatchLoading(false);
                        }
                      }
                    }}
                  >
                    Mute All
                  </Button>
                  <Button 
                    colorScheme="purple" 
                    variant="outline"
                    isLoading={batchLoading}
                    onClick={async () => {
                      if (confirm('Are you sure you want to deafen all members?')) {
                        setBatchLoading(true);
                        try {
                          await deafenMember(groupId, 'all', 'Deafen all members');
                          await loadGroupAndRoom(); // Reload to see updated status
                        } finally {
                          setBatchLoading(false);
                        }
                      }
                    }}
                  >
                    Deafen All
                  </Button>
                  <Button 
                    colorScheme="teal" 
                    variant="outline"
                    isLoading={batchLoading}
                    onClick={async () => {
                      setBatchLoading(true);
                      try {
                        await undeafenMember(groupId, 'all');
                        await loadGroupAndRoom(); // Reload to see updated status
                      } finally {
                        setBatchLoading(false);
                      }
                    }}
                  >
                    Undeafen All
                  </Button>
                </ButtonGroup>
              )}
            </VStack>
          </CardHeader>
          <CardBody>
            <VStack align="stretch" spacing={3}>
              {group.members.members.map((member, index) => (
                <HStack key={index} justify="space-between" p={3} borderRadius="md" bg="gray.50" _dark={{ bg: "gray.700" }}>
                  <HStack>
                    {(isOwner || isAdmin) && member.userprofile_id !== profile?.id && (
                      <Checkbox
                        isChecked={selectedMembers.includes(member.user_id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedMembers([...selectedMembers, member.user_id]);
                          } else {
                            setSelectedMembers(selectedMembers.filter(id => id !== member.user_id));
                          }
                        }}
                      />
                    )}
                    <Avatar
                      size="sm"
                      name={member.userprofile_id}
                      bg={member.is_speaking ? 'green.500' : 'gray.500'}
                      border={member.is_muted ? '2px solid red' : member.is_deafened ? '2px solid orange' : undefined}
                    />
                    <VStack align="start" spacing={0}>
                      <Text fontWeight="medium">{member.userprofile_id}</Text>
                      <HStack spacing={2}>
                        <Badge size="sm" colorScheme="blue">
                          {getMemberRoleLabel(member.role)}
                        </Badge>
                        {member.is_muted && (
                          <Badge size="sm" colorScheme="red">
                            Muted
                          </Badge>
                        )}
                        {member.is_deafened && (
                          <Badge size="sm" colorScheme="orange">
                            Deafened
                          </Badge>
                        )}
                        {member.is_speaking && (
                          <Badge size="sm" colorScheme="green">
                            Speaking
                          </Badge>
                        )}
                      </HStack>
                    </VStack>
                  </HStack>
                  
                  {(isOwner || isAdmin) && member.userprofile_id !== profile?.id && (
                    <Menu>
                      <MenuButton
                        as={IconButton}
                        icon={<FiMoreVertical />}
                        size="sm"
                        variant="ghost"
                      />
                      <MenuList>
                        {member.is_muted ? (
                          <MenuItem 
                            icon={<FiVolume2 />}
                            onClick={async () => {
                              await unmuteMember(groupId, member.user_id);
                              await loadGroupAndRoom();
                            }}
                          >
                            Unmute
                          </MenuItem>
                        ) : (
                          <MenuItem 
                            icon={<FiVolumeX />}
                            onClick={async () => {
                              await muteMember(groupId, member.user_id, 'Muted by admin');
                              await loadGroupAndRoom();
                            }}
                          >
                            Mute
                          </MenuItem>
                        )}
                        
                        {member.is_deafened ? (
                          <MenuItem 
                            icon={<FiHeadphones />}
                            onClick={async () => {
                              await undeafenMember(groupId, member.user_id);
                              await loadGroupAndRoom();
                            }}
                          >
                            Undeafen
                          </MenuItem>
                        ) : (
                          <MenuItem 
                            icon={<FiHeadphones />}
                            onClick={async () => {
                              await deafenMember(groupId, member.user_id, 'Deafened by admin');
                              await loadGroupAndRoom();
                            }}
                          >
                            Deafen
                          </MenuItem>
                        )}
                        
                        {isOwner && (
                          <MenuItem 
                            icon={<FiEdit />}
                            onClick={async () => {
                              if (confirm(`Are you sure you want to transfer ownership to ${member.userprofile_id}?`)) {
                                await transferOwnership(groupId, member.user_id);
                                await loadGroupAndRoom();
                              }
                            }}
                          >
                            Transfer Ownership
                          </MenuItem>
                        )}
                        
                        <MenuItem icon={<FiUserMinus />} color="red.500">
                          Remove from Group
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  )}
                </HStack>
              ))}
            </VStack>
          </CardBody>
        </Card>
      )}
    </Box>
  );
};